/**
 * Chart geometry — scales, stacking math, arc/wedge/line paths and per-chart
 * layout functions. All pixel values mirror the design reference
 * (`reference/lib/xa-charts.jsx`), which is the visual source of truth.
 *
 * Layout functions are pure: (data, config) → view model. The framework
 * wrappers re-run them whenever inputs or the hidden-series set change, then
 * render the result verbatim. This keeps Angular and AngularJS pixel-identical.
 */
import { fmt, fmtFull, niceMax, ticks } from './format.js';
import { resolvePalette, seriesColor, XA_CHROME } from './palettes.js';
import type {
  XaAreaMode,
  XaAxisLabel,
  XaBarGroup,
  XaBarSegment,
  XaCategorical,
  XaDonutArc,
  XaDonutConfig,
  XaDonutLayout,
  XaGridLine,
  XaLegendItem,
  XaLegendLayout,
  XaLegendPos,
  XaLineConfig,
  XaLineLayout,
  XaLineSeriesOut,
  XaPieConfig,
  XaPieInsideLabel,
  XaPieLayout,
  XaPieLeaderLabel,
  XaPieWedge,
  XaPoint,
  XaResolvedSeries,
  XaResolvedSlice,
  XaSlice,
  XaStackedBarConfig,
  XaStackedBarHConfig,
  XaStackedBarHLayout,
  XaStackedBarLayout,
  XaTooltipContent,
  XaTooltipRow,
} from './types.js';

/* ------------------------------------------------------------------ */
/* Interaction constants (from the reference)                          */
/* ------------------------------------------------------------------ */

/** Tooltip offset from the cursor, relative to the chart container. */
export const TOOLTIP_OFFSET = { x: 14, y: 6 } as const;
/** Extra stroke width on a hovered donut arc. */
export const DONUT_HOVER_STROKE_DELTA = 6;
/** Outward translation of a hovered pie wedge, along its mid-angle. */
export const PIE_HOVER_OFFSET = 5;
/** Line/area data point radii: resting, hovered, and "appears on hover" (dots off). */
export const DOT_RADIUS = 2.75;
export const DOT_RADIUS_HOVER = 4.5;
export const DOT_RADIUS_APPEAR = 4;
/** Flat (stacked) area fill opacity. */
export const AREA_FLAT_OPACITY = 0.78;
/** Crosshair stroke + dash (line/area hover). */
export const CROSSHAIR_STROKE = XA_CHROME.crosshair;
export const CROSSHAIR_DASH = '3 3';
/** Gridline dash for line/area charts (bars use solid gridlines). */
export const GRID_DASH = '3 4';

/* ------------------------------------------------------------------ */
/* Shared resolution helpers                                           */
/* ------------------------------------------------------------------ */

function toHiddenSet(hidden?: Iterable<string>): Set<string> {
  return hidden instanceof Set ? (hidden as Set<string>) : new Set(hidden ?? []);
}

export function resolveSeries(
  series: XaCategorical['series'],
  colors: readonly string[],
  hidden?: Iterable<string>,
): XaResolvedSeries[] {
  const off = toHiddenSet(hidden);
  return series.map((s, i) => ({
    name: s.name,
    values: s.values,
    color: seriesColor(s.color, colors, i),
    total: s.values.reduce((a, b) => a + b, 0),
    visible: !off.has(s.name),
  }));
}

export function resolveSlices(
  slices: XaSlice[],
  colors: readonly string[],
  hidden?: Iterable<string>,
): XaResolvedSlice[] {
  const off = toHiddenSet(hidden);
  return slices.map((d, i) => ({
    label: d.label,
    value: d.value,
    color: seriesColor(d.color, colors, i),
    visible: !off.has(d.label),
  }));
}

// Legends have room for the real figure, so they carry the full value (no K/M/B
// abbreviation or rounding) rather than the compact axis-style `fmt`.
function seriesLegend(series: XaResolvedSeries[]): XaLegendItem[] {
  return series.map((s) => ({ label: s.name, color: s.color, value: fmtFull(s.total), visible: s.visible }));
}

function sliceLegend(slices: XaResolvedSlice[]): XaLegendItem[] {
  return slices.map((d) => ({ label: d.label, color: d.color, value: fmtFull(d.value), visible: d.visible }));
}

/**
 * Legend layout per chart/position, exactly as the reference composes it:
 * stacked-bar bottom → column; donut right → column; line/area bottom with
 * values → column; everything else → row.
 */
export function legendLayoutFor(
  chart: 'stacked-bar' | 'stacked-bar-h' | 'donut' | 'pie' | 'line' | 'area',
  pos: XaLegendPos,
  legendValues: boolean,
): XaLegendLayout {
  if (chart === 'stacked-bar' && pos === 'bottom') return 'column';
  if (chart === 'donut') return pos === 'bottom' ? 'row' : 'column';
  if ((chart === 'line' || chart === 'area') && pos === 'bottom' && legendValues) return 'column';
  return 'row';
}

/* ------------------------------------------------------------------ */
/* Axis grid                                                           */
/* ------------------------------------------------------------------ */

function gridLines(max: number, x0: number, x1: number, y: (v: number) => number): XaGridLine[] {
  return ticks(max).map((t, i) => ({
    x0,
    x1,
    y: y(t),
    value: t,
    label: fmt(t),
    labelX: x0 - 8,
    labelY: y(t) + 3.5,
    baseline: i === 0,
    stroke: i === 0 ? XA_CHROME.baseline : XA_CHROME.grid,
  }));
}

/* ------------------------------------------------------------------ */
/* Slanted x-axis label gutter                                         */
/* ------------------------------------------------------------------ */

/** Axis tick font size in px (mirrors `.xac-axis` in styles.ts). */
const AXIS_LABEL_FONT_PX = 10.5;
/**
 * Average glyph advance of the condensed axis font, in `em`. The layout is pure
 * and can't measure the DOM, so label width is estimated from character count;
 * the estimate is deliberately a touch generous to favour a little extra gutter
 * over clipping.
 */
const AXIS_LABEL_CHAR_EM = 0.6;
/** Magnitude of the slant applied to x-axis labels, in degrees (`rotate: -35`). */
const X_LABEL_SLANT_DEG = 35;
/** Slanted-label baseline offset below the plot (`labelY = height - pad.b + 16`). */
const X_LABEL_BASELINE = 16;
/** Breathing room kept under the longest slanted label. */
const X_LABEL_BOTTOM_MARGIN = 8;
/** Previous fixed slanted-label gutter; the floor so short-label charts are unchanged. */
const X_LABEL_MIN_PAD = 52;

/**
 * Bottom padding (px) reserved for slanted x-axis labels, scaled to the longest
 * label so long category names stop clipping. Labels are right-anchored at the
 * tick and rotated `X_LABEL_SLANT_DEG°`, so the longest one drops
 * `sin(slant) · textWidth` below its baseline; the SVG height is fixed, so the
 * gutter must contain that drop. Floored at the previous fixed gutter so charts
 * with short labels render exactly as before.
 */
function slantedXLabelPad(labels: readonly string[]): number {
  const maxChars = labels.reduce((m, s) => Math.max(m, s ? s.length : 0), 0);
  const textW = maxChars * AXIS_LABEL_FONT_PX * AXIS_LABEL_CHAR_EM;
  const drop = textW * Math.sin((X_LABEL_SLANT_DEG * Math.PI) / 180);
  return Math.max(X_LABEL_MIN_PAD, Math.ceil(X_LABEL_BASELINE + drop + X_LABEL_BOTTOM_MARGIN));
}

/* ------------------------------------------------------------------ */
/* Path primitives                                                     */
/* ------------------------------------------------------------------ */

/** Circular arc path (stroked, not filled) from angle a0 to a1, clockwise. */
export function arcPath(cx: number, cy: number, r: number, a0: number, a1: number): string {
  // A full sweep (single visible slice) has identical start/end points, which makes
  // the SVG `A` command render nothing. Draw it as two semicircles so the ring shows.
  if (a1 - a0 >= Math.PI * 2 - 1e-6) {
    const x0 = cx + r * Math.cos(a0), y0 = cy + r * Math.sin(a0);
    const xm = cx + r * Math.cos(a0 + Math.PI), ym = cy + r * Math.sin(a0 + Math.PI);
    return `M ${x0} ${y0} A ${r} ${r} 0 1 1 ${xm} ${ym} A ${r} ${r} 0 1 1 ${x0} ${y0}`;
  }
  const large = a1 - a0 > Math.PI ? 1 : 0;
  const x0 = cx + r * Math.cos(a0), y0 = cy + r * Math.sin(a0);
  const x1 = cx + r * Math.cos(a1), y1 = cy + r * Math.sin(a1);
  return `M ${x0} ${y0} A ${r} ${r} 0 ${large} 1 ${x1} ${y1}`;
}

/** Filled pie wedge: centre → arc start → arc → close. */
export function wedgePath(cx: number, cy: number, r: number, a0: number, a1: number): string {
  const large = a1 - a0 > Math.PI ? 1 : 0;
  const x0 = cx + r * Math.cos(a0), y0 = cy + r * Math.sin(a0);
  const x1 = cx + r * Math.cos(a1), y1 = cy + r * Math.sin(a1);
  return `M ${cx} ${cy} L ${x0} ${y0} A ${r} ${r} 0 ${large} 1 ${x1} ${y1} Z`;
}

/**
 * Polyline → SVG path. With `smooth`, Catmull-Rom-style cubic Béziers
 * (neighbour-difference tangents ÷ 6), as in the reference.
 */
export function buildPath(pts: ReadonlyArray<readonly [number, number]>, smooth: boolean): string {
  if (!pts.length) return '';
  if (!smooth || pts.length < 3) return 'M ' + pts.map((p) => p[0] + ' ' + p[1]).join(' L ');
  let d = `M ${pts[0][0]} ${pts[0][1]}`;
  for (let i = 0; i < pts.length - 1; i++) {
    const p0 = pts[Math.max(i - 1, 0)], p1 = pts[i], p2 = pts[i + 1], p3 = pts[Math.min(i + 2, pts.length - 1)];
    const c1x = p1[0] + (p2[0] - p0[0]) / 6, c1y = p1[1] + (p2[1] - p0[1]) / 6;
    const c2x = p2[0] - (p3[0] - p1[0]) / 6, c2y = p2[1] - (p3[1] - p1[1]) / 6;
    d += ` C ${c1x} ${c1y}, ${c2x} ${c2y}, ${p2[0]} ${p2[1]}`;
  }
  return d;
}

/* ------------------------------------------------------------------ */
/* Stacked bar (vertical)                                              */
/* ------------------------------------------------------------------ */

const BAR_PAD = { l: 44, r: 8, t: 10, b: 26 } as const;

export function computeStackedBar(data: XaCategorical, cfg: XaStackedBarConfig = {}): XaStackedBarLayout {
  const width = cfg.width ?? 520;
  const height = cfg.height ?? 280;
  const variant = cfg.variant ?? 'flat';
  const maxXLabels = cfg.maxXLabels ?? 8;
  const rotateXLabels = cfg.rotateXLabels ?? false;
  const colors = resolvePalette(cfg.colors ?? 'tokens');
  // Slanted labels need extra vertical room below the plot so they clear the
  // legend; horizontal overhang at the first/last category is handled by the
  // wrapper SVG's `overflow: visible`, so no extra side padding is needed here.
  const pad = rotateXLabels ? { ...BAR_PAD, b: slantedXLabelPad(data.categories) } : BAR_PAD;
  const W = width - pad.l - pad.r;
  const H = height - pad.t - pad.b;

  const series = resolveSeries(data.series, colors, cfg.hidden);
  const act = series.filter((s) => s.visible);
  const totals = data.categories.map((_, ci) => act.reduce((a, s) => a + (s.values[ci] || 0), 0));
  const max = niceMax(Math.max(...totals, 1));
  const y = (v: number) => pad.t + H - (v / max) * H;

  const n = data.categories.length;
  const step = n ? W / n : W;
  const bw = Math.min(step * 0.62, 34);
  const gap = variant === 'rounded' ? 2 : 0;
  const cap = variant === 'rounded' ? Math.min(4, bw / 2) : 0;
  // When slanting labels we show every category; otherwise thin wide runs out.
  const labelEvery = rotateXLabels ? 1 : Math.max(1, Math.ceil(n / maxXLabels));
  const labelY = height - pad.b + 16;

  const groups: XaBarGroup[] = data.categories.map((c, ci) => {
    let acc = 0;
    const x = pad.l + ci * step + (step - bw) / 2;
    const segments: XaBarSegment[] = [];
    act.forEach((s, si) => {
      const v = s.values[ci] || 0;
      const y1 = y(acc), y0 = y(acc + v);
      acc += v;
      if (v <= 0) return;
      const isTop = si === act.length - 1;
      segments.push({
        seriesName: s.name,
        color: s.color,
        x,
        y: y0,
        width: bw,
        height: Math.max(y1 - y0 - (isTop ? 0 : gap), 0),
        rx: isTop ? cap : 0,
      });
    });
    return {
      index: ci,
      category: c,
      hitX: pad.l + ci * step,
      hitY: pad.t,
      hitWidth: step,
      hitHeight: H,
      segments,
      total: totals[ci],
      label:
        ci % labelEvery === 0
          ? rotateXLabels
            ? { x: pad.l + ci * step + step / 2, y: labelY, text: c, rotate: -35 }
            : { x: pad.l + ci * step + step / 2, y: height - 8, text: c }
          : null,
    };
  });

  return {
    width,
    height,
    max,
    grid: gridLines(max, pad.l, width - pad.r, y),
    groups,
    series,
    legend: seriesLegend(series),
  };
}

/** Tooltip for a hovered bar group: visible series rows + Total when >1 visible. */
export function stackedBarTooltip(layout: XaStackedBarLayout, index: number): XaTooltipContent {
  const act = layout.series.filter((s) => s.visible);
  const group = layout.groups[index];
  const rows: XaTooltipRow[] = act.map((s) => ({ color: s.color, label: s.name, value: fmtFull(s.values[index] || 0) }));
  if (act.length > 1) rows.push({ label: 'Total', value: fmtFull(group.total) });
  return { title: group.category, rows };
}

/* ------------------------------------------------------------------ */
/* Stacked bar (horizontal)                                            */
/* ------------------------------------------------------------------ */

const BARH_PAD_L = 110;
const BARH_PAD_R = 56;

export function computeStackedBarH(data: XaCategorical, cfg: XaStackedBarHConfig = {}): XaStackedBarHLayout {
  const width = cfg.width ?? 520;
  const rowHeight = cfg.rowHeight ?? 30;
  const colors = resolvePalette(cfg.colors ?? 'tokens');
  const W = width - BARH_PAD_L - BARH_PAD_R;

  const series = resolveSeries(data.series, colors, cfg.hidden);
  const act = series.filter((s) => s.visible);
  const totals = data.categories.map((_, ci) => act.reduce((a, s) => a + (s.values[ci] || 0), 0));
  const max = niceMax(Math.max(...totals, 1));
  const height = data.categories.length * rowHeight + 8;
  const bh = Math.min(rowHeight * 0.58, 18);

  const rows = data.categories.map((c, ci) => {
    let acc = 0;
    const yMid = ci * rowHeight + rowHeight / 2 + 4;
    const segments: XaBarSegment[] = [];
    for (const s of act) {
      const v = s.values[ci] || 0;
      const x0 = BARH_PAD_L + (acc / max) * W;
      const w = (v / max) * W;
      acc += v;
      if (v > 0) {
        segments.push({
          seriesName: s.name,
          color: s.color,
          x: x0,
          y: yMid - bh / 2,
          width: Math.max(w - 1.5, 0),
          height: bh,
          rx: 2,
        });
      }
    }
    return {
      index: ci,
      category: c,
      yMid,
      hitX: 0,
      hitY: ci * rowHeight + 4,
      hitWidth: width,
      hitHeight: rowHeight,
      labelX: BARH_PAD_L - 10,
      labelY: yMid + 3.5,
      trackX0: BARH_PAD_L,
      trackX1: BARH_PAD_L + W,
      segments,
      total: totals[ci],
      totalLabel: fmt(totals[ci]),
      totalX: BARH_PAD_L + (totals[ci] / max) * W + 8,
      totalY: yMid + 3.5,
    };
  });

  return { width, height, max, rows, series, legend: seriesLegend(series) };
}

/** Tooltip for a hovered row: visible series rows (no Total row — matches reference). */
export function stackedBarHTooltip(layout: XaStackedBarHLayout, index: number): XaTooltipContent {
  const act = layout.series.filter((s) => s.visible);
  return {
    title: layout.rows[index].category,
    rows: act.map((s) => ({ color: s.color, label: s.name, value: fmtFull(s.values[index] || 0) })),
  };
}

/* ------------------------------------------------------------------ */
/* Donut                                                               */
/* ------------------------------------------------------------------ */

export function computeDonut(data: XaSlice[], cfg: XaDonutConfig = {}): XaDonutLayout {
  const size = cfg.size ?? 240;
  const thickness = cfg.thickness ?? 0.3;
  const variant = cfg.variant ?? 'solid';
  const colors = resolvePalette(cfg.colors ?? 'brand');

  const slices = resolveSlices(data, colors, cfg.hidden);
  const act = slices.filter((d) => d.visible);
  const total = act.reduce((a, d) => a + d.value, 0);
  const R = size / 2;
  const t = R * thickness;
  // Reserve room for the hover stroke (grows by DONUT_HOVER_STROKE_DELTA) so the
  // fattened hovered arc stays inside the `size` box instead of being clipped:
  // hovered outer edge = r + (t + DONUT_HOVER_STROKE_DELTA) / 2 <= R.
  const r = R - t / 2 - DONUT_HOVER_STROKE_DELTA / 2 - (variant === 'segmented' ? 3 : 1);
  // Solid rings have adjacent arcs meet exactly (no angular gap): butt caps are
  // perpendicular to the tangent, so a trimmed gap on a thick ring leaves a
  // slanted seam between slices. Abutting the fills keeps the boundary a clean
  // straight radial line. The segmented variant keeps its visible round-capped gaps.
  const gapA = variant === 'segmented' ? 0.055 : 0;

  let a = -Math.PI / 2;
  const arcs: XaDonutArc[] = act.map((d) => {
    const sweep = total ? (d.value / total) * Math.PI * 2 : 0;
    const a0 = a + gapA / 2;
    const a1 = a + Math.max(sweep - gapA / 2, 0.001);
    a += sweep;
    return {
      label: d.label,
      value: d.value,
      pct: total ? Math.round((d.value / total) * 100) : 0,
      color: d.color,
      path: arcPath(R, R, r, a0, a1),
    };
  });

  return {
    size,
    cx: R,
    cy: R,
    radius: r,
    strokeWidth: t,
    hoverStrokeWidth: t + DONUT_HOVER_STROKE_DELTA,
    linecap: variant === 'segmented' ? 'round' : 'butt',
    arcs,
    total,
    centerValue: cfg.centerValue != null ? cfg.centerValue : fmt(total),
    centerLabel: cfg.centerLabel ?? 'Total',
    centerValueY: R - 6,
    centerLabelY: R + 16,
    slices,
    legend: sliceLegend(slices),
  };
}

export function donutTooltip(arc: XaDonutArc): XaTooltipContent {
  return { rows: [{ color: arc.color, label: arc.label, value: `${fmtFull(arc.value)} · ${arc.pct}%` }] };
}

/* ------------------------------------------------------------------ */
/* Pie                                                                 */
/* ------------------------------------------------------------------ */

export function computePie(data: XaSlice[], cfg: XaPieConfig = {}): XaPieLayout {
  const size = cfg.size ?? 240;
  const labels = cfg.labels ?? 'inside';
  const colors = resolvePalette(cfg.colors ?? 'mono');

  const slices = resolveSlices(data, colors);
  const total = slices.reduce((a, d) => a + d.value, 0);
  const padOuter = labels === 'leader' ? 56 : 8;
  const R = size / 2;
  const r = R - padOuter;

  let a = -Math.PI / 2;
  const wedges: XaPieWedge[] = slices.map((d) => {
    const sweep = total ? (d.value / total) * Math.PI * 2 : 0;
    const a0 = a;
    const a1 = a + sweep;
    const mid = a + sweep / 2;
    a += sweep;
    return {
      label: d.label,
      value: d.value,
      pct: total ? Math.round((d.value / total) * 100) : 0,
      color: d.color,
      path: wedgePath(R, R, r, a0, a1),
      midAngle: mid,
      ux: Math.cos(mid),
      uy: Math.sin(mid),
    };
  });

  const leaderLabels: XaPieLeaderLabel[] = [];
  const insideLabels: XaPieInsideLabel[] = [];
  for (const w of wedges) {
    if (labels === 'leader' && w.pct >= 4) {
      const x0 = R + (r + 4) * w.ux, y0 = R + (r + 4) * w.uy;
      const x1 = R + (r + 14) * w.ux, y1 = R + (r + 14) * w.uy;
      const right = w.ux >= 0;
      const x2 = x1 + (right ? 8 : -8);
      leaderLabels.push({
        label: w.label,
        points: `${x0},${y0} ${x1},${y1} ${x2},${y1}`,
        textX: x2 + (right ? 4 : -4),
        textY: y1 + 3.5,
        anchor: right ? 'start' : 'end',
        text: `${w.label} · ${w.pct}%`,
      });
    }
    if (labels === 'inside' && w.pct >= 7) {
      insideLabels.push({
        label: w.label,
        x: R + r * 0.62 * w.ux,
        y: R + r * 0.62 * w.uy + 4,
        text: `${w.pct}%`,
      });
    }
  }

  return { size, cx: R, cy: R, radius: r, total, wedges, leaderLabels, insideLabels, slices, legend: sliceLegend(slices) };
}

export function pieTooltip(wedge: XaPieWedge): XaTooltipContent {
  return { rows: [{ color: wedge.color, label: wedge.label, value: `${fmtFull(wedge.value)} · ${wedge.pct}%` }] };
}

/* ------------------------------------------------------------------ */
/* Line / area                                                         */
/* ------------------------------------------------------------------ */

export function computeLine(data: XaCategorical, cfg: XaLineConfig = {}): XaLineLayout {
  const width = cfg.width ?? 520;
  const height = cfg.height ?? 260;
  const smooth = cfg.smooth ?? false;
  const endLabels = cfg.endLabels ?? false;
  const maxXLabels = cfg.maxXLabels ?? 8;
  const rotateXLabels = cfg.rotateXLabels ?? false;
  const stacked = cfg.stacked ?? false;
  const area: XaAreaMode = cfg.area ?? 'none';
  const colors = resolvePalette(cfg.colors ?? 'brand');
  const salt = cfg.gradientSalt ?? '';

  // `contentWidth` is the internal drawing width: when it exceeds the visible
  // `width` the categories spread across a wider SVG (x-axis zoom) and the host
  // scrolls horizontally. Clamped to never shrink below the visible width.
  const contentW = Math.max(cfg.contentWidth ?? width, width);

  // Slanted labels need extra room below the plot so they don't clip. The
  // right gutter must also fit the trailing x-axis label: end-anchored leader
  // labels live in the 110px `endLabels` gutter, but the default middle-anchored
  // tick labels (e.g. a full date) overhang their last point by ~half their
  // width, so mirror the left padding to keep the final label from clipping.
  const pad = { l: 44, r: endLabels ? 110 : 44, t: 10, b: rotateXLabels ? slantedXLabelPad(data.categories) : 26 };
  const W = contentW - pad.l - pad.r;
  const H = height - pad.t - pad.b;

  const series = resolveSeries(data.series, colors, cfg.hidden);
  const act = series.filter((s) => s.visible);
  const n = data.categories.length;

  // Stack bounds [lo, hi] per category × active series.
  const stacks = stacked
    ? data.categories.map((_, ci) => {
        let acc = 0;
        return act.map((s) => {
          const lo = acc;
          acc += s.values[ci] || 0;
          return [lo, acc] as const;
        });
      })
    : null;

  const max = niceMax(
    stacked
      ? Math.max(...data.categories.map((_, ci) => act.reduce((a, s) => a + (s.values[ci] || 0), 0)), 1)
      : Math.max(...act.flatMap((s) => s.values), 1),
  );

  const x = (i: number) => pad.l + (n === 1 ? W / 2 : (i / (n - 1)) * W);
  const y = (v: number) => pad.t + H - (v / max) * H;
  // When slanting labels we show every category; otherwise thin wide runs out.
  const labelEvery = rotateXLabels ? 1 : Math.max(1, Math.ceil(n / maxXLabels));
  const labelY = height - pad.b + 16;

  const xs = data.categories.map((_, i) => x(i));
  const xLabels: XaAxisLabel[] = [];
  data.categories.forEach((c, ci) => {
    if (ci % labelEvery !== 0) return;
    xLabels.push(
      rotateXLabels
        ? { x: x(ci), y: labelY, text: c, rotate: -35 }
        : { x: x(ci), y: height - 8, text: c },
    );
  });

  const seriesOut: XaLineSeriesOut[] = act.map((s, si) => {
    const pts: Array<readonly [number, number]> = data.categories.map((_, ci) =>
      stacked ? ([x(ci), y(stacks![ci][si][1])] as const) : ([x(ci), y(s.values[ci] || 0)] as const),
    );
    const linePath = buildPath(pts, smooth);

    let areaPath: string | null = null;
    if (area !== 'none' && n > 0) {
      if (stacked) {
        // Baseline = previous stack top, traversed back (straight segments — as the reference draws it).
        const base = data.categories.map((_, ci) => [x(ci), y(stacks![ci][si][0])] as const).reverse();
        areaPath = linePath + ' L ' + base.map((p) => p[0] + ' ' + p[1]).join(' L ') + ' Z';
      } else {
        areaPath = linePath + ` L ${x(n - 1)} ${y(0)} L ${x(0)} ${y(0)} Z`;
      }
    }

    return {
      name: s.name,
      color: s.color,
      linePath,
      areaPath,
      gradientId: `xag-${salt ? salt + '-' : ''}${s.name.replace(/\W/g, '')}${si}`,
      gradientStops: [
        { offset: '0%', opacity: stacked ? 0.85 : 0.32 },
        { offset: '100%', opacity: stacked ? 0.85 : 0.04 },
      ] as [{ offset: string; opacity: number }, { offset: string; opacity: number }],
      areaOpacity: area === 'flat' ? AREA_FLAT_OPACITY : 1,
      points: pts.map(([px, py]) => ({ x: px, y: py }) as XaPoint),
      endLabel:
        endLabels && n > 0
          ? { x: pts[n - 1][0] + 10, y: pts[n - 1][1] + 4, text: `${s.name} · ${fmt(s.values[n - 1])}` }
          : null,
    };
  });

  return {
    width: contentW,
    height,
    viewWidth: width,
    gutter: pad.l,
    // Mask stops 4px short of the data (first point/x-label sit at pad.l) while
    // still covering the value labels, which are right-anchored at pad.l - 8.
    axisMaskWidth: pad.l - 4,
    max,
    grid: gridLines(max, pad.l, pad.l + W, y),
    xLabels,
    xs,
    plotTop: pad.t,
    plotBottom: pad.t + H,
    seriesOut,
    series,
    legend: seriesLegend(series),
  };
}

/** Index of the category whose x position is closest to pointer x. */
export function nearestIndex(xs: readonly number[], px: number): number {
  let best = 0;
  let bd = Infinity;
  for (let i = 0; i < xs.length; i++) {
    const d = Math.abs(xs[i] - px);
    if (d < bd) {
      bd = d;
      best = i;
    }
  }
  return best;
}

/** Tooltip at category `index`: one row per visible series. */
export function lineTooltip(layout: XaLineLayout, data: XaCategorical, index: number): XaTooltipContent {
  const act = layout.series.filter((s) => s.visible);
  return {
    title: data.categories[index],
    rows: act.map((s) => ({ color: s.color, label: s.name, value: fmtFull(s.values[index] || 0) })),
  };
}
